// RESIZE the work volume by dragging a wall handle. Each wall moves
// independently — the dragged edge tracks the laser endpoint along its
// axis while the opposite edge is held, so the canvas can grow/shrink on
// any side (and reach any cell count, odd or even). The ceiling drives
// `height`; the floor is fixed. Shrinking past existing voxels is allowed —
// the volume clips whatever ends up outside it (main's applyVolumeBounds);
// the only floor is the 1-cell minimum span enforced there.
//
// The handler doesn't own the bounds — main.ts does. It computes the
// dragged edge's new position and hands it to an `applyEdge` callback so
// the volume rebuild (cell bounds + wall meshes) stays in one place.

import * as THREE from "three";
import { controllerStates } from "../../input/controllers";
import type { VolumeBounds } from "../../scene/work-volume";
import { laserEndpointInGridLocal } from "../laser-endpoint";
import { interaction, setInteraction } from "../state";

const _endpoint = new THREE.Vector3();

// Laser-endpoint coordinate along `axis` in grid-local metres.
function endpointAxis(
  controllerIndex: number,
  grabDistance: number,
  gridGroup: THREE.Object3D,
  axis: 0 | 1 | 2,
): number {
  laserEndpointInGridLocal(controllerIndex, grabDistance, gridGroup, _endpoint);
  return axis === 0 ? _endpoint.x : axis === 1 ? _endpoint.y : _endpoint.z;
}

// Metre position of the wall edge this handle drives.
function edgeOf(b: VolumeBounds, axis: 0 | 1 | 2, sign: -1 | 1): number {
  if (axis === 1) return b.height; // ceiling
  if (axis === 0) return sign > 0 ? b.maxX : b.minX;
  return sign > 0 ? b.maxZ : b.minZ;
}

export function startResizeWall(
  axis: 0 | 1 | 2,
  sign: -1 | 1,
  controllerIndex: number,
  grabDistance: number,
  gridGroup: THREE.Object3D,
  bounds: VolumeBounds,
  axisDriven: boolean,
): void {
  setInteraction({
    kind: "resize_wall",
    controllerIndex,
    axis,
    sign,
    grabDistance,
    startProjection: endpointAxis(controllerIndex, grabDistance, gridGroup, axis),
    startEdge: edgeOf(bounds, axis, sign),
    axisDriven,
  });
}

// Per-frame: move the dragged edge to track the laser endpoint and push it to
// the caller (which clips any voxels left outside the new volume).
export function handleResizeDrag(
  gridGroup: THREE.Object3D,
  applyEdge: (axis: 0 | 1 | 2, sign: -1 | 1, edge: number) => void,
): void {
  if (interaction.kind !== "resize_wall") return;
  const state = interaction;
  if (!controllerStates[state.controllerIndex].selecting) return;
  // Desktop drives the edge from screen drag (updateDesktopResize in main).
  if (state.axisDriven) return;

  const current = endpointAxis(
    state.controllerIndex,
    state.grabDistance,
    gridGroup,
    state.axis,
  );
  const edge = state.startEdge + (current - state.startProjection);
  applyEdge(state.axis, state.sign, edge);
}
